import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import OAuth from '../components/OAuth';
import { createUserWithEmailAndPassword, getAuth, updateProfile } from 'firebase/auth';
import { toast } from 'react-toastify';
import { db } from '../firebase';
import { doc, serverTimestamp, setDoc, getDocs, query, collection, where, orderBy, limit } from 'firebase/firestore';

const galleryImages = [
  'https://us.123rf.com/450wm/microone/microone1710/microone171000540/88416719-sea-illustration-white-sailboat-on-big-waves-boat-in-ocean-vector.jpg?ver=6',
  
];

export default function SignUp() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [latestUsers, setLatestUsers] = useState([]);
  const [formData, setFormData] = useState({
    userCategory: "",
    name: "",
    email: "",
    password: "",
    confirmPassword: ""
  });
  const [isFocused, setIsFocused] = useState({
    name: false,
    email: false,
    password: false,
    confirmPassword: false
  });
  const { userCategory, name, email, password, confirmPassword } = formData;
  const navigate = useNavigate();

  // Fetch the latest 10 users from Firebase
  useEffect(() => {
    const fetchLatestUsers = async () => {
      try {
        const usersQuery = query(
          collection(db, 'users'),
          orderBy('timestamp', 'desc'),
          limit(10)
        );
        const querySnapshot = await getDocs(usersQuery);
        const users = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setLatestUsers(users);
      } catch (error) {
        console.error('Error fetching latest users:', error);
      }
    };

    fetchLatestUsers();
  }, []);

  function onChange(e) {
    setFormData((prevState)=>({ 
      ...prevState,
      [e.target.id]: e.target.value
    }));
  }

  function handleFocus(e) {
    setIsFocused(prev => ({
      ...prev,
      [e.target.id]: true
    }));
  }

  function handleBlur(e) {
    if (!e.target.value) {
      setIsFocused(prev => ({
        ...prev,
        [e.target.id]: false
      }));
    }
  }

  // Function to generate username from name
  async function generateUsername(name) {
    let baseUsername = name.toLowerCase().replace(/\s+/g, '.');
    baseUsername = baseUsername.replace(/[^a-z0-9.]/g, '');
    
    let finalUsername = baseUsername;
    let usernameExists = true;
    let counter = 0;

    while (usernameExists) {
      const q = query(collection(db, "users"), where("username", "==", finalUsername));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        usernameExists = false;
      } else {
        counter++;
        finalUsername = `${baseUsername}${counter}`;
      }
    }

    return finalUsername;
  }

  async function onSubmit(e) {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

     if (password !== confirmPassword) {
    toast.error("Passwords don't match");
    return;
  }
    
    if (!agreeToTerms) {
      toast.error("You must agree to the terms & conditions");
      return;
    }
   
    setIsLoading(true);
    try {
      const auth = getAuth();
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      const username = await generateUsername(name);
      
      await updateProfile(auth.currentUser, {
        displayName: name
      });
      
      const user = userCredential.user;
      const formDataCopy = {
        ...formData,
        username: username,
        timestamp: serverTimestamp(),
        verified: false // Add verification status, default to false
      };
      
      delete formDataCopy.password;
      delete formDataCopy.confirmPassword;

      await setDoc(doc(db, "users", user.uid), formDataCopy);
      toast.success(`Signed you up as a '${userCategory}'`);
      navigate("/add-profile-image");
    } catch (error) {
      // Custom error messages based on Firebase error codes
      switch (error.code) {
        case 'auth/email-already-in-use':
          toast.error("This email is already in use. Please sign in or use a different email.");
          break;
        case 'auth/invalid-email':
          toast.error("The email address is not valid. Please enter a valid email.");
          break;
        case 'auth/weak-password':
          toast.error("Your password is too weak. It should be at least 6 characters long.");
          break;
        case 'auth/operation-not-allowed':
          toast.error("Sign-up is currently disabled. Please try again later.");
          break;
        default:
          toast.error("An error occurred while signing up. Please try again.");
          console.error("Sign-up error:", error); // Log for debugging
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <section className="bg-black min-h-screen relative overflow-hidden">
      {/* Floating movie elements */}
      <div className="absolute top-0 left-0 w-full h-full opacity-3 pointer-events-none">
        {[...Array(10)].map((_, i) => (
          <div 
            key={i}
            className="absolute bg-white rounded-full"
            style={{
              width: `${Math.random() * 6 + 2}px`,
              height: `${Math.random() * 6 + 2}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: `float ${Math.random() * 12 + 8}s linear infinite`,
              animationDelay: `${Math.random() * 3}s`,
              opacity: Math.random() * 0.3 + 0.1
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 pt-4 pb-8">
        <h1 className="text-3xl md:text-4xl text-center font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF1493] via-[#4da3ff] to-pink-400 animate-gradient-x">
          Join the Boat Now!
        </h1>
        <p className="text-center text-gray-400 mt-1 text-sm md:text-base">
          Let's float to the source for exclusive features.
        </p>

        <div className="flex flex-col lg:flex-row justify-center items-center gap-6 mt-6">
          {/* Image Gallery Container with Orbiting Users */}
          <div className="w-full lg:w-1/2 relative h-[280px] md:h-[380px] lg:h-[480px] mb-6 lg:mb-0">
            {/* Orbiting User Profiles */}
            <div className="orbit-container absolute top-0 left-0 w-full h-full z-10">
              {latestUsers.map((user, index) => (
                <div
                  key={user.id}
                  className="user-profile absolute rounded-full border-2 border-white"
                  style={{
                    width: '3rem',
                    height: '3rem',
                    transform: `rotate(${(360 / latestUsers.length) * index}deg) translateX(6rem) rotate(-${(360 / latestUsers.length) * index}deg)`,
                    animation: `orbit ${15 + (index % 5)}s linear infinite`,
                    animationDelay: `${index * 0.5}s`,
                  }}
                >
                  <img
                    src={user.photoURL || 'https://cdn-icons-png.flaticon.com/512/149/149071.png'}
                    alt={user.name || 'User'}
                    className="w-full h-full rounded-full object-cover"
                  />
                </div>
              ))}
            </div>

            <div className="absolute inset-0 overflow-hidden rounded-xl">
              {galleryImages.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`Cinema scene ${index + 1}`}
                  className="absolute transition-all duration-500 ease-out"
                  style={{
                    transition: 'all 0.5s ease',
                    borderRadius: '0.75rem',
                    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.3)',
                    objectFit: 'cover',
                    width: '100%',
                    height: '100%',
                    zIndex: 5,
                    transform: 'scale(1)',
                    filter: 'none',
                    border: '1px solid rgba(255, 20, 147, 0.3)'
                  }}
                />
              ))}
            </div>
          </div>

          {/* Sign Up Form */}
          <div className="w-full lg:w-1/2 lg:max-w-md">
            <form onSubmit={onSubmit} className="bg-gray-900 bg-opacity-70 backdrop-blur-sm p-5 rounded-xl border border-gray-800">
              <div className="relative mb-4">
                <select
                  id="userCategory"
                  value={userCategory}
                  onChange={onChange}
                  required
                  className="w-full px-4 py-3 text-white bg-gray-800 border border-gray-700 rounded-lg transition ease-in-out focus:outline-none focus:ring-2 focus:ring-[#4da3ff]"
                >
                  <option value="" className="text-gray-500">Am a ...</option>
                  <option value="Standard user" className="text-white">Standard user</option>
                  <option value="Seller" className="text-white">Seller</option>
                  <option value="Buyer" className="text-white">Buyer</option>
                </select>
              </div>

             <div className="relative mb-4">
  <input
    type="text"
    id="name"
    value={name}
    onChange={onChange}
    onFocus={handleFocus}
    onBlur={handleBlur}
    maxLength={25}
    className="w-full px-4 py-3 text-white bg-gray-800 border border-gray-700 rounded-lg transition ease-in-out focus:outline-none focus:ring-2 focus:ring-[#4da3ff]"
  />
  <label 
    htmlFor="name"
    className={`absolute left-4 transition-all duration-200 ease-in-out pointer-events-none ${isFocused.name || name ? 'top-1 text-xs text-gray-400' : 'top-3.5 text-gray-500'}`}
  >
    Full name
  </label>
  {name && (
    <span className="absolute right-3 top-3 text-xs text-gray-400">
      {name.length}/25
    </span>
  )}
</div>

              <div className="relative mb-4">
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={onChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  className="w-full px-4 py-3 text-white bg-gray-800 border border-gray-700 rounded-lg transition ease-in-out focus:outline-none focus:ring-2 focus:ring-[#4da3ff]"
                />
                <label 
                  htmlFor="email"
                  className={`absolute left-4 transition-all duration-200 ease-in-out pointer-events-none ${isFocused.email || email ? 'top-1 text-xs text-gray-400' : 'top-3.5 text-gray-500'}`}
                >
                  Email address
                </label>
              </div>

              <div className="relative mb-4">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={onChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  className="w-full px-4 py-3 text-white bg-gray-800 border border-gray-700 rounded-lg transition ease-in-out focus:outline-none focus:ring-2 focus:ring-[#4da3ff]"
                />
                <label 
                  htmlFor="password"
                  className={`absolute left-4 transition-all duration-200 ease-in-out pointer-events-none ${isFocused.password || password ? 'top-1 text-xs text-gray-400' : 'top-3.5 text-gray-500'}`}
                >
                  Password
                </label>
                <span 
                  className="absolute right-3 top-3 text-sm cursor-pointer text-gray-400 hover:text-white"
                  onClick={() => setShowPassword((prevState) => !prevState)}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </span>
              </div>

              <div className="relative mb-4">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={onChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  className="w-full px-4 py-3 text-white bg-gray-800 border border-gray-700 rounded-lg transition ease-in-out focus:outline-none focus:ring-2 focus:ring-[#4da3ff]"
                />
                <label 
                  htmlFor="confirmPassword"
                  className={`absolute left-4 transition-all duration-200 ease-in-out pointer-events-none ${isFocused.confirmPassword || confirmPassword ? 'top-1 text-xs text-gray-400' : 'top-3.5 text-gray-500'}`}
                >
                  Confirm Password
                </label>
                <span 
                  className="absolute right-3 top-3 text-sm cursor-pointer text-gray-400 hover:text-white"
                  onClick={() => setShowConfirmPassword((prevState) => !prevState)}
                >
                  {showConfirmPassword ? 'Hide' : 'Show'}
                </span>
              </div>

              <div className="flex items-center mb-5">
                <input
                  type="checkbox"
                  id="agreeToTerms"
                  checked={agreeToTerms}
                  onChange={(e) => setAgreeToTerms(e.target.checked)}
                  required
                  className="mr-2 h-4 w-4 text-[#4da3ff] focus:ring-[#4da3ff] border-gray-600 rounded"
                />
                <label htmlFor="agreeToTerms" className="text-gray-300 text-sm">
                  I agree to the{' '}
                  <Link 
                    to="/terms-of-service" 
                    className="text-[#4da3ff] hover:text-[#FF1493] transition duration-200 ease-out"
                    target="_blank"
                  >
                    terms & conditions
                  </Link>
                </label>
              </div>

              <button
                className="w-full text-white px-5 py-3 text-sm font-bold uppercase rounded-full shadow-lg transition duration-150 ease-in-out hover:shadow-xl bg-gradient-to-r from-[#FF1493] via-blue-600 to-[#4da3ff] animate-gradient-x flex items-center justify-center"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    SourceAI Verifying...
                  </>
                ) : 'Create Account'}
              </button>

              <div className="flex items-center my-4 before:border-t before:flex-1 before:border-gray-700 after:border-t after:flex-1 after:border-gray-700">
                <p className="text-center font-semibold mx-4 text-gray-400 text-sm">OR</p>
              </div>
              
              <OAuth />

              <div className="flex justify-between whitespace-nowrap text-sm mt-5">
                <p className="text-gray-400">
                  Already have an account?
                  <Link to="/sign-in" className="text-[#FF1493] hover:text-[#4da3ff] transition duration-200 ease-out ml-1">
                    Sign in
                  </Link>
                </p>
                <p className="text-gray-400 hover:text-[#ff0000] transition duration-200 ease-out">
                  <Link to="/forgot-password">Forgot password?</Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx global>{`
        @keyframes float {
          0% { transform: translateY(0) rotate(0deg); opacity: 0.1; }
          50% { opacity: 0.2; }
          100% { transform: translateY(-100vh) rotate(180deg); opacity: 0; }
        }
        @keyframes gradient-x {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes orbit {
          0% { transform: rotate(0deg) translateX(6rem) rotate(0deg); }
          100% { transform: rotate(360deg) translateX(6rem) rotate(-360deg); }
        }
        .animate-gradient-x {
          background-size: 200% 200%;
          animation: gradient-x 3s ease infinite;
        }
        .orbit-container {
          position: absolute;
          width: 100%;
          height: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .user-profile {
          position: absolute;
          width: 3rem;
          height: 3rem;
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 20;
        }
        @media (max-width: 768px) {
          .user-profile {
            width: 2.5rem;
            height: 2.5rem;
          }
          @keyframes orbit {
            0% { transform: rotate(0deg) translateX(4rem) rotate(0deg); }
            100% { transform: rotate(360deg) translateX(4rem) rotate(-360deg); }
          }
        }
      `}</style>
    </section>
  );
}